import { BRAND, type Brand } from '@/lib/cms/config';

/**
 * Appointment reason values accepted by POS Manager / Salesforce.
 *
 * All 10 reasons are shown on both Gallet and Breitling builds: Gallet has no own
 * boutiques — appointments happen at Breitling boutiques (Gallet corners), so
 * customers must be able to pick either brand when booking.
 *
 * @see backends/breitling-pos-manager/src/breitling_pos_manager/appointment/schemas.py
 */
const DISCOVER_REASON_BY_BRAND: Record<Brand, 'discover_and_try' | 'discover_and_try_gallet'> = {
  breitling: 'discover_and_try',
  gallet: 'discover_and_try_gallet',
  ug: 'discover_and_try_gallet',
};

const PURCHASE_REASON_BY_BRAND: Record<Brand, 'purchase' | 'purchase_gallet'> = {
  breitling: 'purchase',
  gallet: 'purchase_gallet',
  ug: 'purchase_gallet',
};

const STRAP_REASON_BY_BRAND: Record<Brand, 'find_a_new_strap' | 'find_a_new_strap_gallet'> = {
  breitling: 'find_a_new_strap',
  gallet: 'find_a_new_strap_gallet',
  ug: 'find_a_new_strap_gallet',
};

/** Same 10 options on both brand builds — each maps to its own SF API value. */
export const APPOINTMENT_REASON_DEFINITIONS = [
  { translationKey: 'discoverAndTryBreitling', value: DISCOVER_REASON_BY_BRAND.breitling },
  { translationKey: 'discoverAndTryGallet', value: DISCOVER_REASON_BY_BRAND.gallet },
  { translationKey: 'discoverBothBrands', value: 'discover_and_try_multibrand' },
  { translationKey: 'purchaseBreitling', value: PURCHASE_REASON_BY_BRAND.breitling },
  { translationKey: 'purchaseGallet', value: PURCHASE_REASON_BY_BRAND.gallet },
  { translationKey: 'findNewStrapBreitling', value: STRAP_REASON_BY_BRAND.breitling },
  { translationKey: 'findNewStrapGallet', value: STRAP_REASON_BY_BRAND.gallet },
  { translationKey: 'pickUp', value: 'boutique_pick_up' },
  { translationKey: 'takeCare', value: 'take_care_of_my_breitling' },
  { translationKey: 'other', value: 'other' },
] as const;

export type AppointmentReasonValue = (typeof APPOINTMENT_REASON_DEFINITIONS)[number]['value'];

export const APPOINTMENT_REASON_VALUES = APPOINTMENT_REASON_DEFINITIONS.map(
  (definition) => definition.value
) as [AppointmentReasonValue, ...AppointmentReasonValue[]];

export type AppointmentReasonTranslationKey =
  (typeof APPOINTMENT_REASON_DEFINITIONS)[number]['translationKey'];

/** Pre-selected discover reason for the site brand (Gallet.com → Gallet collection). */
export const DEFAULT_APPOINTMENT_REASON: AppointmentReasonValue = DISCOVER_REASON_BY_BRAND[BRAND];

const APPOINTMENT_REASON_SET = new Set<string>(APPOINTMENT_REASON_VALUES);

/**
 * Whether a reason string is allowed for POS Manager.
 *
 * Example Input:
 * reason="purchase"
 *
 * Example Output:
 * true (valid on both Gallet and Breitling builds)
 */
export function isAppointmentReasonValue(reason: string): reason is AppointmentReasonValue {
  return APPOINTMENT_REASON_SET.has(reason);
}
